const FONT_TIMEOUT_MS = 1500;
const IMAGE_DECODE_TIMEOUT_MS = 1500;
export const DEFAULT_SETTLE_DELAY_MS = 2500;

function rafTick(win: Window): Promise<void> {
  return new Promise((resolve) => {
    if (typeof win.requestAnimationFrame === 'function') {
      win.requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 16);
    }
  });
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | undefined> {
  return Promise.race<T | undefined>([
    promise.catch(() => undefined),
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), timeoutMs)),
  ]);
}

/**
 * Wait for a capture iframe to settle on its final frame before
 * rasterizing. Resolves after ALL of:
 *
 *   - `document.fonts.ready` (raced against a 1500 ms timeout)
 *   - `<img>.decode()` for every visible image (raced against 1500 ms)
 *   - `delayMs` (default 2500 ms) for CSS animations to land
 *   - 2 `requestAnimationFrame` ticks so any post-animation paint commits
 *
 * Caller is responsible for ensuring the iframe has loaded before calling
 * this — `iframe.contentDocument` must be reachable.
 */
export async function waitForThumbnailSettle(
  iframe: HTMLIFrameElement,
  delayMs: number = DEFAULT_SETTLE_DELAY_MS,
): Promise<void> {
  const win = iframe.contentWindow;
  const doc = iframe.contentDocument;

  const fontsPromise: Promise<unknown> =
    doc && (doc as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready
      ? (doc as Document & { fonts: { ready: Promise<unknown> } }).fonts.ready
      : Promise.resolve();

  const imageDecodePromise: Promise<unknown> = doc
    ? Promise.all(
        Array.from(doc.querySelectorAll('img'))
          .filter((img) => !!(img as HTMLImageElement).src)
          .map((img) => (img as HTMLImageElement).decode().catch(() => undefined)),
      )
    : Promise.resolve();

  const delayPromise = new Promise<void>((resolve) => setTimeout(resolve, delayMs));

  await Promise.all([
    withTimeout(fontsPromise, FONT_TIMEOUT_MS),
    withTimeout(imageDecodePromise, IMAGE_DECODE_TIMEOUT_MS),
    delayPromise,
  ]);

  const tickWin = win ?? window;
  await rafTick(tickWin);
  await rafTick(tickWin);
}
